import { Component, type ElementRef, EventEmitter, Input, NgZone, type OnDestroy, type OnInit, Output, inject, viewChild } from '@angular/core';

declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement, options: any) => string;
      reset?: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

@Component({
  selector: 'app-turnstile',
  template: `<div #turnstileRef></div>`,
  standalone: false
})
export class TurnstileComponent implements OnInit, OnDestroy {
  private zone = inject(NgZone);

  @Input() siteKey!: string;
  @Output() token = new EventEmitter<string>();
  @Output() loaded = new EventEmitter<void>();
  @Output() failed = new EventEmitter<void>();

  readonly elRef = viewChild.required<ElementRef>('turnstileRef');

  private widgetId: string | null = null;
  private retryTimer: any;
  private scriptLoaded = false;

  ngOnInit(): void {
    // Global callback for Turnstile API?onload=onTurnstileLoad
    window.onTurnstileLoad = () => {
      this.handleScriptLoaded();
    };

    // Attempt initial load
    this.loadTurnstileScript().catch(() => this.failed.emit());
  }

  //----------------------------------------
  // Public refresh method for parent component
  //----------------------------------------
  refresh(): void {
    if (window.turnstile?.reset && this.widgetId) {
      window.turnstile.reset(this.widgetId);
    } else {
      this.destroyWidget();
      this.render();
    }
  }

  //----------------------------------------
  // Main render method
  //----------------------------------------
  private render(): void {
    const renderFn = () => {
      this.widgetId = window.turnstile!.render(this.elRef().nativeElement, {
        sitekey: this.siteKey,
        callback: (token: string) => this.zone.run(() => this.token.emit(token)),
        'error-callback': () => {
          this.zone.run(() => {
            console.warn('Turnstile error → refreshing');
            this.token.emit('');
            this.refresh();
          });
        },
        'expired-callback': () => {
          this.zone.run(() => {
            console.log('Turnstile expired → refreshing');
            this.token.emit('');
            this.refresh();
          });
        },
        theme: 'light'
      });
    };

    if (window.turnstile) {
      renderFn();
    } else {
      // Poll until Turnstile API exists
      const check = setInterval(() => {
        if (window.turnstile) {
          clearInterval(check);
          renderFn();
        }
      }, 100);
    }

    // Fallback: if not initialized within 3 seconds → retry
    this.retryTimer = setTimeout(() => {
      if (!this.widgetId) {
        console.warn('Turnstile failed to initialize → retrying');
        this.render();
      }
    }, 3000);
  }

  //----------------------------------------
  // Load Turnstile script dynamically
  //----------------------------------------
  private loadTurnstileScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Already loaded?
      if (window.turnstile) {
        this.handleScriptLoaded();
        resolve();
        return;
      }

      // Script tag already exists?
      const existing = document.getElementById('turnstile-script');
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject());
        return;
      }

      // Create the script tag
      const script = document.createElement('script');
      script.id = 'turnstile-script';
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad';
      script.async = true;
      script.defer = true;

      script.onload = () => resolve();
      script.onerror = () => reject();

      document.body.appendChild(script);

      // Failsafe timeout
      setTimeout(() => {
        if (!window.turnstile) {
          reject();
        }
      }, 6000);
    });
  }

  //----------------------------------------
  // Handle script loaded exactly ONCE
  //----------------------------------------
  private handleScriptLoaded(): void {
    if (this.scriptLoaded) {
      return;
    } // prevent duplicates
    this.scriptLoaded = true;

    this.loaded.emit();
    this.render();
  }

  //----------------------------------------
  // Cleanup
  //----------------------------------------
  private destroyWidget(): void {
    if (window.turnstile && this.widgetId) {
      window.turnstile.remove(this.widgetId);
      this.widgetId = null;
    }
  }

  ngOnDestroy(): void {
    this.destroyWidget();
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
    }
  }
}
